<template>
  <div class="main">
    <h1 id="title">Predicting Shifts In Equilibrium: <span>Q</span> vs <span>K</span></h1>

    <div v-show="!completed">
      <div class="question">
        <div class="equation">
          <span v-if="this.currentQ.a" id="a" class="a" v-html="this.currentQ.a"></span>
          <span class="e" v-if="this.currentQ.abflip">&harr;</span>
          <span v-else-if="this.currentQ.a" class="p">+</span>
          <span id="b" class="b" v-html="this.currentQ.b"></span>
          <span v-if="this.currentQ.abflip" class="p">+</span>
          <span v-else class="e">&harr;</span>
          <span id="ab" class="ab" v-html="this.currentQ.ab"></span>
        </div>
        <div class="concentrations">
          <span v-if="this.currentQ.conc_a" id="conc_a" class="a" v-html="this.currentQ.conc_a"></span>
          <span class="e" v-if="this.currentQ.abflip">&harr;</span>
          <span v-else-if="this.currentQ.a" class="p">+</span>
          <span id="conc_b" class="b" v-html="this.currentQ.conc_b"></span>
          <span v-if="this.currentQ.abflip" class="p">+</span>
          <span v-else class="e">&harr;</span>
          <span id="conc_ab" class="ab" v-html="this.currentQ.conc_ab"></span>
          <span id="k">K = <span v-html="this.currentQ.k"></span></span>
        </div>
      </div>

      <transition name="fadeQuiz">
        <!-- Question 1 -->
        <div v-show="!guessed">
          <div class="guess" :class="hinting ? 'hint' : ''">
          <div class="col-2-3">
            <p>Enter a digit between 1.0 and 9.9</p>
            <div>
              <span>Q =</span> 
              <span id="hint" :class="hinting ? 'hint' : ''">
                <span class="fraction" :class="this.currentQ.abflip ? 'abflip' : ''">
                  <span id="hinttop" v-html="hintTop"></span>
                  <span id="hintbot" v-html="hintBottom"></span>
                </span>
                <span class="equals">=</span>
              </span>
              <input v-model="answer" placeholder="Type in your answer" type="number" min="1" max="9.9" step="0.1" class="num" :class="errorAnswer ? 'error' : ''" v-on:click="checkError('answer')" :disabled="submitText=='Continue' ? 'disabled' : false">
              <span class="times">&times;</span>
            </div>
          </div>
          <div class="col-1-3">
            <p>Exponent</p>
            <div>
              <span class="number">10 <input v-model="exponent" placeholder="Exponent" type="number" class="exp" min="-9" max="9" step="1" :class="errorExponent ? 'error' : ''" v-on:click="checkError('exponent')" :disabled="submitText=='Continue' ? 'disabled' : false"></span>
            </div>
          </div>
          </div>
          <div class="submits" v-show="submitText=='Continue'">
            <p>Correct answer shown, you may continue.</p>
          </div>
          <div class="submits">
            <button class="btn submit" v-on:click="submitOne" :disabled="submitText=='Try Again' ? 'disabled' : false">{{ submitText }}</button> 
            <a class="btn alt" href="#" v-on:click="showHint">Show Hint</a>
          </div>
        </div>
      </transition>

      <transition name="fadeQuiz">
        <!-- Question 2 and Results -->
        <div v-show="guessed">
          <div class="guess guessed">
            <div>
              <span>Q = </span>
              <span class="number">{{answer}}</span>
              <span class="number">&times;</span>
              <span class="number">10<sup>{{exponent}}</sup></span>
            </div>
          </div>
          <div class="guess-second" :class="q2error ? 'q2error' : ''">
            <p><span v-show="submitText=='Submit'" class="preq2">Correct!</span> Question 2: In which direction will equilibrium shift?</p>

            <input type="radio" v-model="direction" value="left" id="left" :disabled="guessed2">
            <label for="left" :class="classLeft">
              <span class="label">A</span>
              <span v-if="missed=='left'">
                Since Q is greater than K there are initially too many products and too few reactants present. The reaction will shift left to produce more reactants until equilibrium is reached.
              </span>
              <span v-else>Since Q is greater than K, equilibrium will shift left towards the reactants.</span>
            </label>

            <input type="radio" v-model="direction" value="right" id="right" :disabled="guessed2">
            <label for="right" :class="classRight">
              <span class="label">B</span>
              <span v-if="missed=='right'">
                Since Q is less than K there are initially too many reactants and too few products present. The reaction will shift right to produce more products until equilibrium is reached.
              </span>
              <span v-else>Since Q is less than K, equilibrium will shift right towards the products.</span>
            </label>

            <input type="radio" v-model="direction" value="equal" id="equal" :disabled="guessed2">
            <label for="equal" :class="classEqual">
              <span class="label">C</span>
              <span v-if="missed=='equal'">Since Q is equal to K there is an equal amount of reactants and products, the reaction is at equilibrium.</span>
              <span v-else>Since Q is equal to K, the reaction is at equilibrium.</span>
            </label>
          </div>
          <!-- Q2 submit or Results -->
          <div class="submits" v-show="!guessed2">
            <button class="btn" v-on:click="submitTwo">Submit</button> 
          </div>
          <div class="results" v-show="guessed2">
            <div class="col-1-3 right" :class="this.currentQ.shift === 'right' ? '' : 'fade'">
              <p>Too much reactant</p>
              <div class="bars">
                <div class="q" style="height:30%;"><span>Q</span></div>
                <div class="k" style="height:60%;"><span>K</span></div>
              </div>
              <p>If Q &lt; K<sub>eq</sub>, shift to right, toward product</p>
            </div>
            <div class="col-1-3 equal" :class="this.currentQ.shift === 'equal' ? '' : 'fade'">
              <p>Equilibrium</p>
              <div class="bars">
                <div class="q" style="height:60%;"><span>Q</span></div>
                <div class="k" style="height:60%;"><span>K</span></div>
              </div>
            </div>
            <div class="col-1-3 left" :class="this.currentQ.shift === 'left' ? '' : 'fade'">
              <p>Too much product</p>
              <div class="bars">
                <div class="q" style="height:90%;"><span>Q</span></div>
                <div class="k" style="height:60%;"><span>K</span></div>
              </div>
              <p>If Q &gt; K<sub>eq</sub>, shift to left, toward reactant</p>
            </div>
          </div>
          <div class="submits" v-show="guessed2 && (alright||missed)">
            <button class="btn" v-on:click="nextQuestion" v-if="alright">Correct! Next Question</button>
            <button class="btn" v-on:click="nextQuestion" v-if="missed">Next Question</button>
          </div>
        </div>
      </transition>
    </div>

    <div v-show="completed" class="completed">
      <div class="question">
        <div class="equation">
          <span>Quiz complete!</span>
        </div>
        <div class="concentrations">
          <span>You got {{correct}} out of {{this.questions.length}}!</span>
        </div>
        <div class="submits">
          <button class="btn" v-on:click="resetEverything">Restart Quiz</button>
        </div>
      </div>
    </div>

    <div class="debug" v-if="debug">
      debug:<br>
      #{{this.currentCount+1}} out of {{this.questions.length}}, score: {{correct}}<br>
      Q = {{this.currentQ.answer}} &times; 10<sup>{{this.currentQ.exponent}}</sup><br>
      shift: {{this.currentQ.shift}}
    </div>

  </div>
</template>

<script>
export default {
  name: 'quiz',
  data: function () {
    return {
      debug: false,
      hinting: false,
      guessed: false,
      guessed2: false,
      alright: false,
      wrongs: 0,
      missed: '',
      answer: '',
      exponent: '',
      direction: '',
      currentQ: false,
      currentCount: 0,
      correct: 0,
      errorExponent: false,
      errorAnswer: false,
      q2error: false,
      completed: false,
      submitText: 'Submit'
    }
  },
  props: [
    'questions',
    'startedQuiz'
  ],
  computed: {
    classLeft: function () {
      var c = []
      if (this.direction === 'left' && this.missed) { c.push('wrong') }
      if (this.direction !== 'left' && this.missed === 'left') { c.push('missed') }
      if (this.guessed2 === true) { c.push('guessed2') }
      return c
    },
    classRight: function () {
      var c = []
      if (this.direction === 'right' && this.missed) { c.push('wrong') }
      if (this.direction !== 'right' && this.missed === 'right') { c.push('missed') }
      if (this.guessed2 === true) { c.push('guessed2') }
      return c
    },
    classEqual: function () {
      var c = []
      if (this.direction === 'equal' && this.missed) { c.push('wrong') }
      if (this.direction !== 'equal' && this.missed === 'equal') { c.push('missed') }
      if (this.guessed2 === true) { c.push('guessed2') }
      return c
    },
    hintBottom: function () {
      if (this.currentQ.a) { var exp1 = this.currentQ.a.match(/(\d)(A|B)/) }
      if (exp1) { exp1 = '<sup>' + exp1[1] + '</sup>' } else { exp1 = '' }
      var exp2 = this.currentQ.b.match(/(\d)(A|B)/)
      if (exp2) { exp2 = '<sup>' + exp2[1] + '</sup>' } else { exp2 = '' }

      var r = ''
      if (this.currentQ.abflip) {
        r = '[' + this.currentQ.a.replace(/(\d)(A|B)/, '$2') + ']' + exp1
      } else {
        if (this.currentQ.a) {
          r = '[' + this.currentQ.a.replace(/(\d)(A|B)/, '$2') + ']' + exp1
        }
        r += '[' + this.currentQ.b.replace(/(\d)(A|B)/, '$2') + ']' + exp2
      }
      return r
    },
    hintTop: function () {
      var exp1 = this.currentQ.ab.match(/(\d)(A|B)/)
      if (exp1) { exp1 = '<sup>' + exp1[1] + '</sup>' } else { exp1 = '' }
      var exp2 = this.currentQ.b.match(/(\d)(A|B)/)
      if (exp2) { exp2 = '<sup>' + exp2[1] + '</sup>' } else { exp2 = '' }

      var r = ''
      if (this.currentQ.abflip) {
        r = '[' + this.currentQ.b.replace(/(\d)(A|B)/, '$2') + ']' + exp2
        r += '[' + this.currentQ.ab.replace(/(\d)(A|B)/, '$2') + ']' + exp1
      } else {
        r = '[' + this.currentQ.ab.replace(/(\d)(A|B)/, '$2') + ']' + exp1
      }
      return r
    }
  },
  methods: {
    gowhee: function (to, duration) {
      to = Math.ceil(to / 10) * 10 // round to nearest 10
      var element = document.documentElement
      var difference = to - element.scrollTop
      var perTick = difference / duration * 10
      var my = this
      setTimeout(function () {
        // stop when you get there
        if (element.scrollTop === to || duration <= 0) return
        element.scrollTop = element.scrollTop + perTick
        my.gowhee(to, duration - 10)
      }, 10)
    },
    showHint: function (e) {
      e.preventDefault()
      this.hinting = !this.hinting
    },
    submitOne: function (e) {
      e.preventDefault()

      if (this.answer === '' || this.exponent === '') {
        if (this.answer === '') { this.errorAnswer = true }
        if (this.exponent === '') { this.errorExponent = true }
        return false
      }

      if (parseFloat(this.answer) !== this.currentQ.answer ||
          parseFloat(this.exponent) !== this.currentQ.exponent) {
        if (parseFloat(this.answer) !== this.currentQ.answer) { this.errorAnswer = true }
        if (parseFloat(this.exponent) !== this.currentQ.exponent) { this.errorExponent = true }
        this.hinting = true
        this.wrongs++
        if (this.wrongs > 1) {
          this.answer = this.currentQ.answer
          this.exponent = this.currentQ.exponent
          this.submitText = 'Continue'
        } else {
          this.submitText = 'Try Again'
        }
        return false
      }

      this.guessed = true

      var my = this
      setTimeout(function () {
        my.gowhee(document.documentElement.offsetHeight, 250)
      }, 100)
    },
    checkError: function (el) {
      if (this.errorAnswer && el === 'answer') { this.errorAnswer = false }
      if (this.errorExponent && el === 'exponent') { this.errorExponent = false }
      this.submitText = 'Submit'
    },
    submitTwo: function (e) {
      e.preventDefault()

      var my = this
      if (this.direction === '') {
        this.q2error = true
        setTimeout(function () {
          my.q2error = false
        }, 1000)
        return false
      }

      this.guessed2 = true

      if (this.direction !== this.currentQ.shift) {
        this.missed = this.currentQ.shift
        setTimeout(function () {
          my.gowhee(document.documentElement.offsetHeight, 1000)
        }, 1500)
      } else {
        this.alright = true
        this.correct++
        setTimeout(function () {
          my.gowhee(document.documentElement.offsetHeight, 250)
        }, 1500)
      }
    },
    nextQuestion: function (e) {
      this.currentCount++
      this.guessed = this.guessed2 = false
      this.hinting = false
      this.errorExponent = this.errorAnswer = false
      this.alright = false
      this.missed = ''
      this.wrongs = 0
      this.submitText = 'Submit'
      this.answer = ''
      this.exponent = ''
      this.direction = ''

      if (this.currentCount === this.questions.length) {
        // done!
        this.completed = true
      } else {
        // next question
        this.currentQ = this.questions[this.currentCount]
        var my = this
        setTimeout(function () {
          my.gowhee(0, 250)
        }, 100)
      }
    },
    resetEverything: function () {
      this.completed = false
      this.$emit('restartQuiz')
      this.currentCount = this.correct = 0
      this.currentQ = this.questions[this.currentCount]
    }
  },
  created: function () {
    this.currentQ = this.questions[this.currentCount]
    // toggle debug mode with ctrl+shift+space
    var my = this
    window.addEventListener('keydown', function (e) {
      if (e.keyCode === 32 && e.shiftKey && e.ctrlKey) {
        my.debug = !my.debug
      }
    })
  }
}
</script>

<style scoped lang="scss">
@import "../assets/styles.scss";

#title { 
  text-transform: uppercase;
  font: bold 18px $font-family;
  letter-spacing: 3px;
  color: $grey;
  margin: 75px 0 85px;
  height: 25px;
  span { font-size: 1.1em; }
}

.question {
  display: flex;
  width: 880px;
  margin: 10px auto;
  align-items:center;
  justify-content: center;  
  flex-direction: column;
}

.equation {
  font: bold 40px $font-family;
  color: $grey;
  letter-spacing: 3px;
}
.concentrations {
  font: normal 24px $font-family;
  color: $dk_grey;
  letter-spacing: 3px;
}
.equation, .concentrations {
  display: block;
  width: 100%;
  display: flex;
  padding: 10px 0;
  span { 
    display: inline-block;
    width: 120px;
    text-align: center;
    sub, sup { font-size: 0.65em; }
    &.e { 
      font-weight: bold !important; 
      width: 100px;
    }
    &.p { width: 80px; }
  }
}
span#k { 
  width: 240px; 
  text-align: right; 
  margin-left: 0px;
  span { width: auto; }
}

.guess {
  display: flex;
  margin: 50px;
  width: 650px;
  transition: all 0.5s ease-in-out;
  &.hint { 
    width: 900px; 
    transition: all 0.25s linear;
  }
  line-height: 60px !important;
  .col-2-3 { 
    flex: 2; 
    padding-left: 10px;
  }
  .col-1-3 { flex: 1; }

  span {
    color: $dk_grey;
    font: bold 48px $font-family;
    &.number {
      font: normal 52px $font-family;
    }
  }
  .times { 
    float: right; 
    padding-right: 20px; 
    font: normal 60px $font-family;
  }
  input {
    border: none;
    padding: 0;
    border-bottom: solid 2px $grey;
    font: normal 48px/48px $font-family;   
    color: $grey;
    width: 200px;
    height: 50px;
    text-align: center;
    &::placeholder {
      color: $grey;
      opacity: 0.6;
      font: normal 22px/48px $font-family;
      letter-spacing: -1px;
      vertical-align: bottom;
    }
    &.exp { 
      width: 100px;
      padding: 5px 0;
      font: normal 38px/40px $font-family;
      position: relative;
      top: -20px;
    }
    &.error {
      outline: solid 2px #c88;
      &::placeholder { color: #c88; }
    }
    &:disabled {
      background: #fff;
    }
  }
}

#hint {
  padding: 10px 1px;
  span {
    font: bold 38px $font-family;
    color: $grey;
    letter-spacing: 3px;
    display: inline-block;
  }
  &> span { width: 1px; }
  opacity: 0;
  transition: all 0.5s linear;
  .fraction { transition: width 0.5s 0s, opacity 0.25s 0s; }
  &.hint {
    padding: 10px 15px;
    opacity: 1;
    .equals { width: 10px; }
    .fraction { 
      width: 180px; 
      opacity: 1;
      transition: width 0.5s 0s, opacity 0.25s 0.25s;
      &.abflip { width: 200px; }
    }
  }

}
.fraction {
  display: inline-block;
  position: relative;
  letter-spacing: 2px;
  white-space: nowrap;
  opacity: 0;
  span { 
    text-align: center; 
    width: 100%; 
    display: block;
    padding: 5px 0;
  }
}
#hinttop {
  position: absolute;
  border-bottom: solid 2px $grey;
  top: -35px;
}
#hintbot {
  position: relative;
  bottom: -35px;
}

.guessed {
  display: block;
  width: 100%;
  text-align: center;
  letter-spacing: 3px;
  margin-bottom: 10px;
  span.number { 
    font: bold 42px $font-family; 
    color: $grey;
    sup { font-size: 0.75em; }
  }
}
.guess-second {
  width: 800px;
  margin: 0 auto;
  p { 
    text-align: center;
    font: normal 24px $font-family;
    color: $dk_grey !important;
    letter-spacing: 2px;
  }
  label {
    display: block;
    border: solid 2px $grey;
    margin: 30px 0;
    cursor: pointer;
    transition: all 0.2s linear;
    vertical-align: top;
    &:hover { opacity: 0.7; }
    span {
      vertical-align: top;
      font: normal 20px/44px $font-family;
      padding: 0;
      display: inline-block;
      padding: 10px 20px;
      color: $dk_grey;
      width: 680px;
      &.label {
        width: 80px;
        line-height: 65px;
        height: 65px;
        padding: 0;
        text-align: center;
        color: $blue;
        border-right: solid 2px $grey;
      }
    }
    &.missed {
      color: $blue;
      border-color: $blue;
      cursor: default;
      opacity: 1;
      span { 
        padding: 20px;
        &.label { 
          padding: 0;
          background: $blue;
          color: #fff;
          border-right-color: $blue; 
          border-bottom: solid 2px $blue;
        }
      }
    }
  }
  input { display: none; }
  input:checked + label {
    border-color: $blue;
    &.guessed2 { opacity: 1; }
    &:hover { opacity: 1; cursor: default; }
    span.label {
      border-right-color: $blue;
      background: $blue;
      color: #fff;
    }
  }
  input:checked + label.wrong {
    border-color: #f88;
    cursor: default;
    opacity: 1;
    span.label {
      border-right-color: #f88;
      background: #f88;
      color: #fff;
    }
  }
  label.guessed2 {
    opacity: 0.5;
    cursor: default;
  }
  label.guessed2.wrong, label.guessed2.missed { opacity: 1; }
}

.results {
  display: flex;
  margin: 50px 0;
  .col-1-3 {
    flex: 0 1 auto;
    text-align: center;
    width: 33%;

    &.fade {
      .bars, p { opacity: 0.35; }
    }

    .bars {
      height: 300px;
      width: 250px;
      display: flex;
      margin: 0 auto;
      .q, .k {
        display: flex;
        margin: 0 10px;
        width: 50%;
        align-self: flex-end;
        display: block;
        color: #fff;
        position: relative;
        span {
          width: 100%;
          position: absolute;
          bottom: 20px;
          left: 0;
          font-size: 24px;
        }
      }
      .q { background: #944; }
      .k { background: #46a; }
    }
    .bars + p {
      margin: 40px auto;
      width: 220px;
    }
    &:nth-child(2) {
      position: relative;
      &:before, &:after {
        position: absolute;
        top: 50%;
        font-size: 60px;
        color: $grey;
      }
      &:after {
        right: -30px;
        content: "\2190";
      }
      &:before {
        content: "\2192";
        left: -30px;
      }
    }
  }
}
.q2error.guess-second {
  p { 
    color: #c88 !important; 
    text-decoration: underline;
  }
  label {
    border-color: #c88;
    span { border-color: #c88; color: #c88; }
    animation: bump 0.2s;
    &:nth-of-type(1) { animation-delay: 0s; }
    &:nth-of-type(2) { animation-delay: 0.1s; }
    &:nth-of-type(3) { animation-delay: 0.2s; }
  }
}
@keyframes bump {
  0% { transform: translateY(0px) }
  80% { transform: translateY(-10px) }
  100% { transform: translateY(0px) }
}
.completed span { width: 100%; }
.submits { text-align: center; margin: 20px auto 50px; }
.btn.alt {
  border: none;
  background: none;
  color: $grey;
  text-decoration: underline;
  text-transform: none;
  letter-spacing: 1px;
}
.btn.submit {
  width: 200px;
  padding: 12px;
}
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
input[type="number"] {
    -moz-appearance: textfield;
    margin: 0;
}
.debug { position: fixed; bottom: 10px; right: 10px; padding: 10px; border: solid 1px #000; background: #eee; }
span.preq2 { color: $blue !important; display: block; }
input:-ms-input-placeholder {
      color: $grey !important;
      opacity: 0.6 !important;
      font-size: 22px !important;
}
input:-moz-placeholder,
input:placeholder-shown {
      color: $grey !important;
      opacity: 0.6 !important;
      font-size: 22px !important;
}
</style>



// WEBPACK FOOTER //
// Quiz.vue?5e53413c